/**
 * How far a period's extraction has actually got, and what that says about Mapping.
 *
 * Shared because two surfaces answer the same question one click apart — the in-period stepper
 * (`useWorkflowStages`) and the periods list (`ProjectListV2/periodSteps`) — and they used to answer
 * it differently: one read the aggregate `*_progress_status` fields, the other inferred from where
 * the cursor sat. The same period then read "Finished" on one and "Start Extraction" on the other.
 *
 * The rules here are deliberately evidence-first. Both fields they replace report progress on
 * periods whose extraction list is empty, which is what painted "Finished" over a page counting 0.
 */

/**
 * Anything the pipeline has touched: run, failed, warned, or currently running.
 *
 * `extractionRecordCount` — saved extractions counted off the extract list — is added to the same
 * total rather than replacing the stats, because either one is proof on its own and neither is
 * complete: the stats are absent on periods holding extractions, and the records say nothing about
 * a run still in flight.
 */
export const extractionActivityCount = (period: any): number => {
  const stats = period?.extraction_stats;
  const fromStats =
    (stats?.successful_extractions_number ?? 0) +
    (stats?.warned_extractions_number ?? 0) +
    (stats?.failed_extractions_number ?? 0) +
    (stats?.in_progress_extractions_number ?? 0);
  const fromRecords =
    typeof period?.extractionRecordCount === 'number' ? period.extractionRecordCount : 0;
  return Math.max(fromStats, fromRecords);
};

/**
 * How many extractions this period could have — the denominator behind the Extraction page's
 * "0 of 7". One per (sheet × category) pair.
 *
 * `mappedPairCount` is that number counted off the sheet-category rows, and is the only source that
 * can answer for a period carrying no stats at all. It costs a request per period, so the stepper
 * passes it and the periods list does not; the list falls back to what the record already holds and
 * is correspondingly blind on stat-less periods.
 */
export const potentialExtractionCount = (period: any): number => {
  if (period?.mappedPairCount > 0) return period.mappedPairCount;
  const stats = period?.extraction_stats;
  const queued =
    (stats?.not_completed_extractions_number ?? 0) + (stats?.in_progress_extractions_number ?? 0);
  if (queued > 0) return queued;
  // `file_sheet_stats` counts sheets rather than pairs, so it is the roughest of the three.
  const sheetCount = period?.file_sheet_stats?.sheet_count ?? 0;
  const notMapped = period?.file_sheet_stats?.not_mapped_sheet_count ?? 0;
  return Math.max(0, sheetCount - notMapped);
};

/**
 * Nothing extracted, and something there to extract. Measured, so it outranks both the rolled-up
 * status and the cursor's position — the two things that previously claimed otherwise.
 *
 * False when there is no evidence either way (no stats, no mapping rows): silence is not proof that
 * nothing ran, and the record's own status is a better answer than a guess.
 */
export const extractionNotStarted = (period: any): boolean =>
  // Never claimed on a guess. `extractionRecordCount` is the count of saved extractions, and until
  // a surface has actually fetched it, "no stats" means "no information" — not "nothing ran". This
  // guard is what stops the rule firing on a period holding extractions whose stats are absent.
  typeof period?.extractionRecordCount === 'number' &&
  extractionActivityCount(period) === 0 &&
  potentialExtractionCount(period) > 0;

/**
 * Counts alone are not enough: the detailed stats are fetched for automation periods only, and an
 * initial period carries none — asking it for counts answers 0 however much has been extracted. The
 * progress status is what those periods do carry, and is trusted only where nothing measured
 * contradicts it.
 */
export const hasExtractionStarted = (period: any): boolean => {
  if (extractionActivityCount(period) > 0) return true;
  if (extractionNotStarted(period)) return false;
  return (
    !!period?.extraction_progress_status && period.extraction_progress_status !== 'Not started'
  );
};

/**
 * The Mapping step's word. Extraction is the proof that mapping is done — nothing extracts from a
 * sheet that was never mapped — so the first extraction of any kind settles it.
 *
 * `category_mapping_progress_status` is not consulted: it has been seen reading "Not started" while
 * a period's sheets were visibly mapped, and "Completed" on a period with no extractions at all.
 */
export const mappingProgressSubLabel = (period: any): 'Finished' | 'In progress' =>
  hasExtractionStarted(period) ? 'Finished' : 'In progress';

/**
 * The Mapping step's word for an automation period. Mapping there is copied in from the previous
 * period (`Setup/index.jsx`'s carry effect) the moment the file/sheet pairing lands in Source
 * Files, not built one sheet at a time by hand — so one sheet holding a category is the automation
 * having run, and that settles it. A sheet the carry couldn't pair (new this period, no match in
 * the last one) is left for the reader to map by hand without holding up the step over it.
 */
export const mappingProgressSubLabelForAutomation = (period: any): 'Finished' | 'Not started' => {
  const sheetCount = period?.file_sheet_stats?.sheet_count ?? 0;
  const notMapped = period?.file_sheet_stats?.not_mapped_sheet_count ?? 0;
  // No sheets exist yet, or every sheet is still unmapped — either way, nothing has been mapped.
  return notMapped === sheetCount ? 'Not started' : 'Finished';
};

/**
 * The Mapping step's word for whichever kind of period this is — the one thing both the stepper
 * and the periods list should call, so an initial and an automation period keep being told apart
 * the same way in both places rather than each surface making its own guess.
 *
 * Same guard `useWorkflowStages` already uses at its own automation checks: `template_type` has
 * to be present AND say so. Absent — an older payload, a hand-built test fixture — defaults to
 * the initial-period rule instead of assuming the newer one off silence.
 */
export const mappingProgressSubLabelForPeriod = (
  period: any
): 'Finished' | 'In progress' | 'Not started' => {
  const isAutomation = !!period?.template_type && period.template_type !== 'sratch';
  return isAutomation
    ? mappingProgressSubLabelForAutomation(period)
    : mappingProgressSubLabel(period);
};

export type ReportStatus = 'generating' | 'generated' | 'failed' | 'not-started';

/**
 * The one rule for a period's report, checked in this order:
 *   1. `report_task.status` "In progress"                 → generating
 *   2. `report_progress_status` "In progress"/"Completed" → generated
 *   3. `report_task.status` "Failed"                      → failed
 *   4. otherwise                                          → not-started
 *
 * `report_progress_status` is the only proof a report exists. `report_task` is null on scratch
 * periods, on conversion periods, and on automation periods whose report was built outside
 * automate-report, so a null task never means "no report". "not-started" reads as Generate only
 * where the report is the period's next step; each surface decides that.
 */
export const reportStatus = (period: any): ReportStatus => {
  const taskStatus = period?.report_task?.status;
  if (taskStatus === 'In progress') return 'generating';
  const progress = period?.report_progress_status;
  if (progress === 'In progress' || progress === 'Completed') return 'generated';
  if (taskStatus === 'Failed') return 'failed';
  return 'not-started';
};

export const isReportGenerated = (period: any): boolean => reportStatus(period) === 'generated';
